import React, { useRef, useEffect } from 'react';
import * as THREE from 'three';
import './threeBackground.css';
import { useTheme } from '../../context/ThemeContext';

// ── tunables ────────────────────────────────────────────────────────────────
const PARTICLE_COUNT  = 130;
const SPREAD          = 8;
const Z_SPREAD        = SPREAD * 0.35;      // flatter z depth
const Z_HALF          = Z_SPREAD / 2;       // ≈ 1.4
const CONNECTION_DIST = 2.2;
const REPEL_RADIUS    = 2.0;
const REPEL_STRENGTH  = 0.07;
const RETURN_SPEED    = 0.028;
const DAMPING         = 0.80;
const MAX_LINES       = (PARTICLE_COUNT * (PARTICLE_COUNT - 1)) / 2;
const DEPTH_MIN       = 0.08;              // dimmest particles/lines at far z

const ThreeBackground: React.FC = () => {
  const mountRef = useRef<HTMLDivElement>(null);
  const { darkMode } = useTheme();

  useEffect(() => {
    if (!mountRef.current) return;
    const currentMount = mountRef.current;

    // ── scene / camera / renderer ─────────────────────────────────────────
    const scene  = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(75, window.innerWidth / window.innerHeight, 0.1, 1000);
    camera.position.z = 5;

    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    renderer.setSize(window.innerWidth, window.innerHeight);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    currentMount.appendChild(renderer.domElement);

    // Base RGB channel value for full-brightness in each mode
    const baseR = darkMode ? 1.0 : 0.13;
    const baseG = darkMode ? 1.0 : 0.13;
    const baseB = darkMode ? 1.0 : 0.13;

    // ── particles ─────────────────────────────────────────────────────────
    const origins:    THREE.Vector3[] = [];
    const velocities: THREE.Vector3[] = [];
    const particleArr   = new Float32Array(PARTICLE_COUNT * 3);
    const particleColor = new Float32Array(PARTICLE_COUNT * 3);

    for (let i = 0; i < PARTICLE_COUNT; i++) {
      const v = new THREE.Vector3(
        (Math.random() - 0.5) * SPREAD,
        (Math.random() - 0.5) * SPREAD,
        (Math.random() - 0.5) * Z_SPREAD,
      );
      origins.push(v.clone());
      velocities.push(new THREE.Vector3());
      particleArr[i * 3]     = v.x;
      particleArr[i * 3 + 1] = v.y;
      particleArr[i * 3 + 2] = v.z;
      // initial depth color
      const t = Math.max(0, Math.min(1, (v.z + Z_HALF) / Z_SPREAD));
      const b = DEPTH_MIN + (1 - DEPTH_MIN) * t;
      particleColor[i * 3]     = baseR * b;
      particleColor[i * 3 + 1] = baseG * b;
      particleColor[i * 3 + 2] = baseB * b;
    }

    const particlesGeo  = new THREE.BufferGeometry();
    const particlesAttr = new THREE.BufferAttribute(particleArr, 3);
    const particleColorAttr = new THREE.BufferAttribute(particleColor, 3);
    particlesGeo.setAttribute('position', particlesAttr);
    particlesGeo.setAttribute('color', particleColorAttr);

    const particlesMat = new THREE.PointsMaterial({
      size: 0.045,
      vertexColors: true,
      transparent: true,
      opacity: 0.9,
      blending: THREE.AdditiveBlending,
    });
    scene.add(new THREE.Points(particlesGeo, particlesMat));

    // ── constellation lines ───────────────────────────────────────────────
    const lineArr   = new Float32Array(MAX_LINES * 6);
    const lineColor = new Float32Array(MAX_LINES * 6); // same layout as positions
    const linesGeo  = new THREE.BufferGeometry();
    const linesAttr      = new THREE.BufferAttribute(lineArr, 3);
    const linesColorAttr = new THREE.BufferAttribute(lineColor, 3);
    linesGeo.setAttribute('position', linesAttr);
    linesGeo.setAttribute('color', linesColorAttr);
    linesGeo.setDrawRange(0, 0);

    const linesMat = new THREE.LineBasicMaterial({
      vertexColors: true,
      transparent: true,
      opacity: darkMode ? 0.22 : 0.32,
      blending: THREE.NormalBlending,
    });
    scene.add(new THREE.LineSegments(linesGeo, linesMat));

    // ── input state ───────────────────────────────────────────────────────
    const mouse = new THREE.Vector2(99999, 99999);
    let scrollSpeed = 0;
    let lastScrollY = window.scrollY;
    let time = 0;

    const handleMouseMove = (e: MouseEvent) => {
      const halfH = camera.position.z * Math.tan(THREE.MathUtils.degToRad(75 / 2));
      const aspect = window.innerWidth / window.innerHeight;
      mouse.x = ((e.clientX / window.innerWidth)  * 2 - 1) * halfH * aspect;
      mouse.y = (-(e.clientY / window.innerHeight) * 2 + 1) * halfH;
    };

    const handleScroll = () => {
      const delta = Math.abs(window.scrollY - lastScrollY);
      scrollSpeed = Math.min(scrollSpeed + delta * 0.006, 1.0);
      lastScrollY = window.scrollY;
    };

    document.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('scroll', handleScroll);

    // ── animation loop ────────────────────────────────────────────────────
    const pos = particleArr;
    const col = particleColor;

    const animate = () => {
      requestAnimationFrame(animate);

      scrollSpeed *= 0.93;
      time += 0.0008 + scrollSpeed * 0.012;
      const cosT = Math.cos(time);
      const sinT = Math.sin(time);

      for (let i = 0; i < PARTICLE_COUNT; i++) {
        // Drift: slowly orbit origins around Y axis
        const ox = origins[i].x;
        const oz = origins[i].z;
        const driftX = ox * cosT - oz * sinT;
        const driftZ = ox * sinT + oz * cosT;
        const driftY = origins[i].y;

        // Mouse repel
        const dx = pos[i * 3]     - mouse.x;
        const dy = pos[i * 3 + 1] - mouse.y;
        const dist = Math.sqrt(dx * dx + dy * dy);
        if (dist < REPEL_RADIUS && dist > 0.001) {
          const force = (1 - dist / REPEL_RADIUS) * REPEL_STRENGTH;
          velocities[i].x += (dx / dist) * force;
          velocities[i].y += (dy / dist) * force;
        }

        // Spring back
        velocities[i].x += (driftX - pos[i * 3])     * RETURN_SPEED;
        velocities[i].y += (driftY - pos[i * 3 + 1]) * RETURN_SPEED;
        velocities[i].z += (driftZ - pos[i * 3 + 2]) * RETURN_SPEED;
        velocities[i].multiplyScalar(DAMPING);

        pos[i * 3]     += velocities[i].x;
        pos[i * 3 + 1] += velocities[i].y;
        pos[i * 3 + 2] += velocities[i].z;

        // Depth fade: map z → [DEPTH_MIN, 1.0]
        const t = Math.max(0, Math.min(1, (pos[i * 3 + 2] + Z_HALF) / Z_SPREAD));
        const b = DEPTH_MIN + (1 - DEPTH_MIN) * t;
        col[i * 3]     = baseR * b;
        col[i * 3 + 1] = baseG * b;
        col[i * 3 + 2] = baseB * b;
      }

      particlesAttr.needsUpdate    = true;
      particleColorAttr.needsUpdate = true;

      // Constellation lines with per-vertex depth color
      let lineIdx = 0;
      const lp = lineArr;
      const lc = lineColor;

      for (let i = 0; i < PARTICLE_COUNT && lineIdx < MAX_LINES; i++) {
        for (let j = i + 1; j < PARTICLE_COUNT && lineIdx < MAX_LINES; j++) {
          const ddx = pos[i * 3]     - pos[j * 3];
          const ddy = pos[i * 3 + 1] - pos[j * 3 + 1];
          const ddz = pos[i * 3 + 2] - pos[j * 3 + 2];
          if (ddx * ddx + ddy * ddy + ddz * ddz < CONNECTION_DIST * CONNECTION_DIST) {
            // positions
            lp[lineIdx * 6]     = pos[i * 3];
            lp[lineIdx * 6 + 1] = pos[i * 3 + 1];
            lp[lineIdx * 6 + 2] = pos[i * 3 + 2];
            lp[lineIdx * 6 + 3] = pos[j * 3];
            lp[lineIdx * 6 + 4] = pos[j * 3 + 1];
            lp[lineIdx * 6 + 5] = pos[j * 3 + 2];
            // colors — use each endpoint's depth brightness
            lc[lineIdx * 6]     = col[i * 3];
            lc[lineIdx * 6 + 1] = col[i * 3 + 1];
            lc[lineIdx * 6 + 2] = col[i * 3 + 2];
            lc[lineIdx * 6 + 3] = col[j * 3];
            lc[lineIdx * 6 + 4] = col[j * 3 + 1];
            lc[lineIdx * 6 + 5] = col[j * 3 + 2];
            lineIdx++;
          }
        }
      }
      linesGeo.setDrawRange(0, lineIdx * 2);
      linesAttr.needsUpdate      = true;
      linesColorAttr.needsUpdate = true;

      renderer.render(scene, camera);
    };

    animate();

    const handleResize = () => {
      camera.aspect = window.innerWidth / window.innerHeight;
      camera.updateProjectionMatrix();
      renderer.setSize(window.innerWidth, window.innerHeight);
    };
    window.addEventListener('resize', handleResize);

    return () => {
      if (currentMount.contains(renderer.domElement)) {
        currentMount.removeChild(renderer.domElement);
      }
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('scroll', handleScroll);
      document.removeEventListener('mousemove', handleMouseMove);
      particlesGeo.dispose();
      particlesMat.dispose();
      linesGeo.dispose();
      linesMat.dispose();
      renderer.dispose();
    };
  }, [darkMode]);

  useEffect(() => {
    const canvas = mountRef.current?.querySelector('canvas');
    if (canvas) {
      canvas.style.opacity = '0';
      setTimeout(() => { canvas.style.opacity = '1'; }, 300);
    }
  }, [darkMode]);

  return <div className="three-bg" ref={mountRef}></div>;
};

export default ThreeBackground;
